"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import type {
  CInteractiveMapArea,
  CInteractiveMapContent,
} from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { cInteractiveMapTranslations } from "./CInteractiveMap.i18n";
import styles from "./CInteractiveMap.module.css";

/**
 * CInteractiveMap - visitor view komponentu `c_interactive_map` (Mapa interaktywna).
 *
 * **Data source:** `C_INTERACTIVE_MAP_CONTROLLER::getApiContent()` zwraca:
 *   ```
 *   {
 *     id, symbol,
 *     maps:   { base, highlighted },              // 2 wersje obrazka
 *     areas:  [{ id, name, shape, coords, url, tooltip }],
 *     config: { open_url_in_new_tab, show_tooltip_arrow, tooltip_animation_type }
 *   }
 *   ```
 *
 * **Migracja z legacy Smarty (`c_interactive_map_default_modern.imp`):**
 *   - HTML `<area>` + jq.imagemapster (canvas highlight + boundList) -> SVG
 *     overlay sterowane state'em React. SVG `viewBox` rowna sie natywnym
 *     wymiarom obrazka (odczyt z `onLoad`), `preserveAspectRatio="none"`
 *     skaluje SVG razem z obrazem.
 *   - tooltipster ($.fn.tooltipster) -> prosty floating div pozycjonowany przez
 *     centroid hover'owanego obszaru (left/top w % natywnych pikseli).
 *   - jQuery search keyup -> useState + useMemo filtrowanie.
 *   - Hover na liscie bocznej podswietla obszar na mapie (i odwrotnie).
 *   - Highlighted image (per-area composite, jak `imagemapster.render_highlight.altImage`):
 *     wewnatrz SVG renderujemy `<image href={highlighted}>` przyciety przez
 *     `<clipPath>` zawierajacy shape WYLACZNIE hover'owanego obszaru. Bazowy
 *     `<img>` zostaje pelnoekranowo pod spodem - widzimy go wszedzie poza
 *     hover'owanym shape'em (tam wybija sie pixel-perfect highlighted). Bez
 *     globalnego swap'u obrazka.
 *
 * **Hoisting note:** Helpery (`AreaShape`, `ShapePrimitive`, `parseCoords`,
 * `chunks`, `getCenter`) zdefiniowane PRZED `CInteractiveMap` - Next.js/SWC
 * Fast Refresh w trybie dev mial problem z forward references function
 * declarations w client component (`Uncaught ReferenceError: AreaShape is not
 * defined`).
 *
 * **Security:** `area.url` z admin panelu Imperial (trusted) - otwieramy
 * przez `window.open` z `noopener,noreferrer`.
 */

/** Mapowanie `tooltip_animation_type` (z konfiguracji komponentu w panelu) na
 *  klase CSS Module z keyframes. Parytet z tooltipster sideTip - te same 5
 *  typow animacji co opcje selecta w c_interactive_map_admin. */
const ANIM_CLASS_BY_TYPE: Record<string, string> = {
  fade: styles.animFade,
  grow: styles.animGrow,
  swing: styles.animSwing,
  slide: styles.animSlide,
  fall: styles.animFall,
};

/** Parsuje "x1,y1,x2,y2,..." z `<area coords>` na tablice liczb. */
function parseCoords(coords: string): number[] {
  if (!coords) return [];
  return coords
    .split(/[,\s]+/)
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n));
}

function chunks<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/** Srodek obszaru (do pozycjonowania tooltipa). Dla poly: srednia z wierzcholkow. */
function getCenter(
  area: CInteractiveMapArea,
): { x: number; y: number } | null {
  const nums = parseCoords(area.coords);
  if (nums.length === 0) return null;

  if (area.shape === "rect" && nums.length >= 4) {
    return { x: (nums[0] + nums[2]) / 2, y: (nums[1] + nums[3]) / 2 };
  }
  if (area.shape === "circle" && nums.length >= 2) {
    return { x: nums[0], y: nums[1] };
  }
  const points = chunks(nums, 2);
  if (points.length === 0) return null;
  const sum = points.reduce(
    (acc, [x, y]) => ({ x: acc.x + x, y: acc.y + y }),
    { x: 0, y: 0 },
  );
  return { x: sum.x / points.length, y: sum.y / points.length };
}

/** Renderuje shape (rect/circle/polygon) z `<area>` coords w SVG user space. */
function ShapePrimitive({
  area,
  className,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: {
  area: CInteractiveMapArea;
  className?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onClick?: () => void;
}) {
  const nums = parseCoords(area.coords);
  if (nums.length === 0) return null;
  const props = { className, onMouseEnter, onMouseLeave, onClick };

  if (area.shape === "rect" && nums.length >= 4) {
    const [x1, y1, x2, y2] = nums;
    return (
      <rect
        x={Math.min(x1, x2)}
        y={Math.min(y1, y2)}
        width={Math.abs(x2 - x1)}
        height={Math.abs(y2 - y1)}
        {...props}
      />
    );
  }
  if (area.shape === "circle" && nums.length >= 3) {
    return <circle cx={nums[0]} cy={nums[1]} r={nums[2]} {...props} />;
  }
  if (nums.length >= 6) {
    const points = chunks(nums, 2)
      .map((p) => p.join(","))
      .join(" ");
    return <polygon points={points} {...props} />;
  }
  return null;
}

function AreaShape({
  area,
  active,
  hasHighlightedImage,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: {
  area: CInteractiveMapArea;
  active: boolean;
  hasHighlightedImage: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
}) {
  const cls = active
    ? hasHighlightedImage
      ? `${styles.areaShape} ${styles.areaShapeActiveOutline}`
      : `${styles.areaShape} ${styles.areaShapeActive}`
    : area.url
      ? `${styles.areaShape} ${styles.areaShapeLink}`
      : styles.areaShape;
  return (
    <ShapePrimitive
      area={area}
      className={cls}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    />
  );
}

export function CInteractiveMap({ content }: CInteractiveMapProps) {
  const t = useT(cInteractiveMapTranslations);
  const clipIdBase = useId();
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // Obraz mapy moze byc juz w cache i jego `load` zdazyl wystrzelic ZANIM
  // React zhydratuje komponent - wtedy `onLoad` na <img> nigdy nie odpali, a
  // `natural` zostalby `null` -> SVG z hotspotami sie nie renderuje -> brak
  // hover na mapie + brak cross-couplingu z lista. Lapiemy ten przypadek
  // sprawdzajac `img.complete` po hydration.
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
      setNatural({ w: img.naturalWidth, h: img.naturalHeight });
    }
  }, [content?.maps?.base?.src]);

  const areas = content?.areas ?? [];

  const sortedAreas = useMemo(
    () => [...areas].sort((a, b) => a.name.localeCompare(b.name)),
    [areas],
  );

  const filteredAreas = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q === "") return sortedAreas;
    return sortedAreas.filter((a) => a.name.toLowerCase().includes(q));
  }, [sortedAreas, search]);

  if (!content || typeof content !== "object") {
    return <div className={styles.empty}>{t.empty}</div>;
  }
  if (!content.maps?.base?.src) {
    return <div className={styles.empty}>{t.emptyMap}</div>;
  }

  const baseSrc = content.maps.base.src;
  const highlightedSrc = content.maps.highlighted?.src ?? null;
  const cfg = content.config;
  const linkTarget = cfg.open_url_in_new_tab ? "_blank" : "_self";

  const hoveredArea =
    hoveredId !== null ? areas.find((a) => a.id === hoveredId) ?? null : null;
  const tooltipCenter = hoveredArea && natural ? getCenter(hoveredArea) : null;

  const activate = (area: CInteractiveMapArea) => {
    if (!area.url) return;
    window.open(area.url, linkTarget, "noopener,noreferrer");
  };

  // Parytet z tooltipster sideTip - 5 typow animacji. Nieznana wartosc spada
  // do `fade` (sensowny default zamiast braku animacji).
  const animationClass =
    ANIM_CLASS_BY_TYPE[cfg.tooltip_animation_type] ?? styles.animFade;
  const tooltipClassName = [
    styles.tooltip,
    cfg.show_tooltip_arrow && styles.tooltipArrow,
    animationClass,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={styles.container}>
      <div className={styles.search}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder={t.search}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={t.search}
        />
        <ul className={styles.boundList}>
          {filteredAreas.length === 0 ? (
            <li className={styles.listEmpty}>{t.noResults}</li>
          ) : (
            filteredAreas.map((area) => (
              <li
                key={area.id}
                className={
                  hoveredId === area.id
                    ? `${styles.listItem} ${styles.listItemActive}`
                    : styles.listItem
                }
                onMouseEnter={() => setHoveredId(area.id)}
                onMouseLeave={() =>
                  setHoveredId((id) => (id === area.id ? null : id))
                }
                onFocus={() => setHoveredId(area.id)}
                onBlur={() =>
                  setHoveredId((id) => (id === area.id ? null : id))
                }
                onClick={() => activate(area)}
                role={area.url ? "button" : undefined}
                tabIndex={area.url ? 0 : -1}
                onKeyDown={(e) => {
                  if (!area.url) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    activate(area);
                  }
                }}
              >
                {area.name}
              </li>
            ))
          )}
        </ul>
      </div>

      <div className={styles.mapWrap}>
        <div className={styles.imageStack}>
          <img
            ref={imgRef}
            src={baseSrc}
            alt=""
            className={styles.image}
            onLoad={(e) => {
              const img = e.currentTarget;
              if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                setNatural({ w: img.naturalWidth, h: img.naturalHeight });
              }
            }}
          />
          {natural && (
            <svg
              className={styles.overlay}
              viewBox={`0 0 ${natural.w} ${natural.h}`}
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              {/* clipPath dla hover'owanego obszaru. */}
              {highlightedSrc && hoveredArea && (
                <defs>
                  <clipPath
                    id={`${clipIdBase}-hover`}
                    clipPathUnits="userSpaceOnUse"
                  >
                    <ShapePrimitive area={hoveredArea} />
                  </clipPath>
                </defs>
              )}
              {/* Highlighted image przyciety do hover'owanego shape'u. */}
              {highlightedSrc && hoveredArea && (
                <image
                  href={highlightedSrc}
                  x={0}
                  y={0}
                  width={natural.w}
                  height={natural.h}
                  preserveAspectRatio="none"
                  clipPath={`url(#${clipIdBase}-hover)`}
                  pointerEvents="none"
                />
              )}
              {/* Hit-detection + outline. Renderujemy hover'owany jako ostatni
                  zeby outline byl na wierzchu i nie przykrywal go inny shape. */}
              {areas.length > 0 &&
                [...areas]
                  .sort((a, b) =>
                    a.id === hoveredId ? 1 : b.id === hoveredId ? -1 : 0,
                  )
                  .map((area) => (
                    <AreaShape
                      key={area.id}
                      area={area}
                      active={hoveredId === area.id}
                      hasHighlightedImage={highlightedSrc !== null}
                      onMouseEnter={() => setHoveredId(area.id)}
                      onMouseLeave={() =>
                        setHoveredId((id) => (id === area.id ? null : id))
                      }
                      onClick={() => activate(area)}
                    />
                  ))}
            </svg>
          )}
          {hoveredArea?.tooltip && tooltipCenter && natural && (
            <div
              key={hoveredArea.id}
              className={tooltipClassName}
              style={{
                left: `${(tooltipCenter.x / natural.w) * 100}%`,
                top: `${(tooltipCenter.y / natural.h) * 100}%`,
              }}
              role="tooltip"
            >
              {hoveredArea.tooltip.image_src && (
                <img
                  src={hoveredArea.tooltip.image_src}
                  alt=""
                  className={styles.tooltipImage}
                />
              )}
              <h3 className={styles.tooltipTitle}>{hoveredArea.name}</h3>
              {hoveredArea.tooltip.description && (
                <p className={styles.tooltipDesc}>
                  {hoveredArea.tooltip.description}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export type CInteractiveMapProps = {
  content: CInteractiveMapContent;
};
